import { Box, RateSelection as DSRateSelection, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { forwardRef, memo, type Ref } from 'react';
import { useAverageRate, useRateFloor } from '~/domain/Jobs';
import { UserProfession } from '~/enums/UserProfession';
import { useDefaultRecommendedRate } from '../hooks/useDefaultRecommendedRate';
import { getRateLikelihood } from './rateLikelihood';

interface IRateSelectionProps {
  value?: number | null;
  onChange?: (value: number) => void;
  professionType: string;
  officeId: string;
  hasError?: boolean;
}

const RATE_STEP = 1;

/**
 * Professional-pay rate stepper for the new shift-request form. Owns the domain
 * logic — the office's recommended rate, the rate floor/ceiling clamping and the
 * rate→likelihood mapping — and delegates all presentation (the −/+ stepper, the
 * `$value`, the likelihood indicator and helper line) to the DS `RateSelection`.
 */
const Component = (
  { value, onChange, professionType, officeId }: IRateSelectionProps,
  ref: Ref<HTMLDivElement>,
) => {
  const { data: averageRating } = useAverageRate(officeId);

  const professionTypeCode = UserProfession.getCode(professionType) as string | undefined;
  const recommendation = professionTypeCode ? averageRating?.[professionTypeCode] : undefined;
  const profRate = recommendation || { average: 49, min: 18, max: 100 };
  const averageRate = Math.round(profRate.average);

  const { effectiveMin } = useRateFloor({
    professionCode: professionTypeCode,
    averageRate: profRate.average,
    apiMin: profRate.min,
  });

  // Default the rate to the office's recommended (average) rate, matching the
  // legacy field. Only when we have a real recommendation.
  useDefaultRecommendedRate({
    value,
    officeId,
    recommendedRate: recommendation ? averageRate : null,
    onChange,
  });

  const hasValue = value != null;
  // The DS block always renders a `$value`; fall back to the recommended rate
  // until the field is defaulted (e.g. before an office is selected).
  const displayValue = hasValue ? value : averageRate;
  const canDecrement = Boolean(officeId) && hasValue && value > effectiveMin;
  const canIncrement = Boolean(officeId) && hasValue && value < profRate.max;

  const handleDecrement = () => {
    onChange?.(Math.max(effectiveMin, (value ?? effectiveMin) - RATE_STEP));
  };

  const handleIncrement = () => {
    onChange?.(Math.min(profRate.max, (value ?? averageRate) + RATE_STEP));
  };

  const likelihood = getRateLikelihood(displayValue, averageRate);

  return (
    <Box pt={8}>
      {/* Section header — same format as the "Hours" section. */}
      <Typography as="span" bold size="heading_3" color={colors.neutral[600]}>
        Professional Pay
      </Typography>
      <Box style={{ height: '1.5px', backgroundColor: colors.neutral[100], marginTop: 12 }} />

      <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
        <DSRateSelection
          ref={ref}
          data-testid="rate-selection"
          data-cy="rate-selection"
          value={displayValue}
          likelihood={likelihood}
          onDecrement={handleDecrement}
          onIncrement={handleIncrement}
          decrementDisabled={!canDecrement}
          incrementDisabled={!canIncrement}
        />
      </Box>
    </Box>
  );
};

export const RateSelection = memo(forwardRef(Component));
